import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, TrendingUp, TrendingDown, CheckCircle, Target, Shield, BarChart3, Zap, Loader2, AlertTriangle, Calculator, Activity, DollarSign } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { API, getAuthHeaders } from '../utils/api';
import SignalStrengthIndicator from '../components/SignalStrengthIndicator';

// Map TradeForge markets to TradingView symbols
const getTradingViewSymbol = (market) => {
  const symbolMap = {
    'XAUUSD': 'OANDA:XAUUSD',
    'EURUSD': 'FX:EURUSD',
    'GBPUSD': 'FX:GBPUSD',
    'USDJPY': 'FX:USDJPY',
    'BTCUSD': 'BITSTAMP:BTCUSD',
    'US30': 'DJ:DJI'
  };
  return symbolMap[market] || `FX:${market}`;
};

export default function SignalDetail() {
  const { signalId } = useParams();
  const navigate = useNavigate();
  const [signal, setSignal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartUrl, setChartUrl] = useState(null);
  
  // Native Trading state
  const [tradingAccount, setTradingAccount] = useState(null);
  const [showTradeDialog, setShowTradeDialog] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [riskPercent, setRiskPercent] = useState(1.0);
  const [calculatedLotSize, setCalculatedLotSize] = useState(0.01);

  useEffect(() => {
    const fetchSignal = async () => {
      try {
        const response = await axios.get(`${API}/signals/${signalId}`, { withCredentials: true, headers: getAuthHeaders() });
        setSignal(response.data);
        setChartUrl(`${API}/signals/${signalId}/chart?t=${Date.now()}`);
      } catch (error) {
        console.error('Failed to fetch signal:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSignal();
    fetchTradingAccount();
  }, [signalId]);

  // Fetch native trading account
  const fetchTradingAccount = async () => {
    try {
      const res = await axios.get(`${API}/trading/account`, { 
        withCredentials: true, 
        headers: getAuthHeaders() 
      });
      if (res.data) {
        setTradingAccount(res.data);
      }
    } catch (error) {
      console.log('Failed to fetch trading account:', error);
    }
  };

  // Calculate lot size based on risk
  useEffect(() => {
    if (signal && tradingAccount) {
      const entryPrice = parseFloat(signal.entry_zone?.split('-')[0]) || 0;
      const stopLoss = parseFloat(signal.stop_loss) || 0;
      const pipDiff = Math.abs(entryPrice - stopLoss);
      
      if (pipDiff > 0) {
        const riskAmount = tradingAccount.balance * (riskPercent / 100);
        const pipValue = signal.market?.includes('JPY') ? 0.01 : 0.0001;
        const calculatedLot = (riskAmount / (pipDiff / pipValue * 10)) || 0.01;
        setCalculatedLotSize(Math.max(0.01, Math.min(10, parseFloat(calculatedLot.toFixed(2)))));
      }
    }
  }, [signal, riskPercent, tradingAccount]);

  // Handle Trade button click
  const handleTradeClick = async () => {
    if (!tradingAccount) {
      await fetchTradingAccount();
    }
    setShowTradeDialog(true);
  };

  // Execute trade via native trading system
  const handleExecuteTrade = async () => {
    setExecuting(true);
    try {
      await axios.post(`${API}/trading/order`, {
        market: signal.market,
        direction: signal.direction,
        lot_size: calculatedLotSize,
        entry_price: parseFloat(signal.entry_zone?.split('-')[0]) || null,
        stop_loss: parseFloat(signal.stop_loss),
        take_profit: parseFloat(signal.take_profit),
        order_type: 'market'
      }, { withCredentials: true, headers: getAuthHeaders() });
      
      toast.success(`Trade executed! ${signal.direction.toUpperCase()} ${signal.market} @ ${calculatedLotSize} lots`);
      setShowTradeDialog(false);
      fetchTradingAccount(); // Refresh account
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to execute trade');
    } finally {
      setExecuting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-slate-400 dark:text-slate-400">Loading signal...</p>
        </div>
      </div>
    );
  }

  if (!signal) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400 dark:text-slate-400 text-lg">Signal not found</p>
        <Button onClick={() => navigate('/signals')} className="mt-4">Back to Signals</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="signal-detail-page">
      {/* Back button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate('/signals')}
        data-testid="back-to-signals-btn"
        className="text-slate-400 hover:text-white dark:hover:text-white hover:bg-zinc-800 dark:hover:bg-zinc-800"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Signals
      </Button>

      {/* Signal header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-4xl font-bold font-['Manrope'] font-mono text-zinc-900 dark:text-white">{signal.market}</h1>
            <Badge className={`text-lg px-3 py-1 ${
              signal.direction === 'buy' 
                ? 'bg-emerald-400/10 text-emerald-400 ring-emerald-400/20' 
                : 'bg-red-400/10 text-red-400 ring-red-400/20'
            } ring-1 ring-inset`}>
              {signal.direction.toUpperCase()}
            </Badge>
          </div>
          <p className="text-slate-400 dark:text-slate-400">Signal ID: {signal.signal_id}</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Trade Button */}
          <Button 
            onClick={handleTradeClick}
            className={`${signal.direction === 'buy' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'} font-semibold px-6 py-3`}
            data-testid="trade-btn"
          >
            <Zap className="w-5 h-5 mr-2" />
            Trade Now
          </Button>
          {signal.direction === 'buy' ? (
            <TrendingUp className="w-16 h-16 text-emerald-500" />
          ) : (
            <TrendingDown className="w-16 h-16 text-red-500" />
          )}
        </div>
      </div>

      {/* Execute Trade Dialog */}
      <Dialog open={showTradeDialog} onOpenChange={setShowTradeDialog}>
        <DialogContent className="bg-white dark:bg-[#121214] border-slate-200 dark:border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-zinc-900 dark:text-white">
              <Zap className={signal?.direction === 'buy' ? 'text-emerald-400' : 'text-red-400'} />
              Execute Trade - {signal?.market}
            </DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">
              Trade using your TradeForge paper trading account
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 mt-4">
            {/* Account Balance */}
            {tradingAccount && (
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-blue-400" />
                    <span className="text-slate-600 dark:text-slate-300">Account Balance</span>
                  </div>
                  <span className="text-xl font-bold text-blue-400 font-mono">
                    ${tradingAccount.balance?.toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Free margin: ${tradingAccount.free_margin?.toFixed(2)}
                </p>
              </div>
            )}

            {/* Trade Summary */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-100 dark:bg-zinc-900 rounded-lg p-3">
                <p className="text-xs text-slate-500 dark:text-slate-500">Direction</p>
                <p className={`font-bold ${signal?.direction === 'buy' ? 'text-emerald-500' : 'text-red-500'}`}>
                  {signal?.direction?.toUpperCase()}
                </p>
              </div>
              <div className="bg-slate-100 dark:bg-zinc-900 rounded-lg p-3">
                <p className="text-xs text-slate-500 dark:text-slate-500">Entry Zone</p>
                <p className="font-mono text-zinc-900 dark:text-white">{signal?.entry_zone?.split('-')[0]}</p>
              </div>
              <div className="bg-slate-100 dark:bg-zinc-900 rounded-lg p-3">
                <p className="text-xs text-slate-500 dark:text-slate-500">Stop Loss</p>
                <p className="font-mono text-red-500">{signal?.stop_loss}</p>
              </div>
              <div className="bg-slate-100 dark:bg-zinc-900 rounded-lg p-3">
                <p className="text-xs text-slate-500 dark:text-slate-500">Take Profit</p>
                <p className="font-mono text-emerald-500">{signal?.take_profit}</p>
              </div>
            </div>

            {/* Risk Management */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Calculator className="w-4 h-4 text-amber-400" />
                <Label className="text-slate-700 dark:text-slate-300">Risk Management</Label>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs text-slate-500 dark:text-slate-500">Risk Percentage (%)</Label>
                <div className="flex items-center gap-2">
                  {[0.5, 1, 2, 3].map((val) => (
                    <Button
                      key={val}
                      type="button"
                      variant={riskPercent === val ? "default" : "outline"}
                      size="sm"
                      onClick={() => setRiskPercent(val)}
                      className={riskPercent === val ? 'bg-blue-600' : 'border-slate-300 dark:border-zinc-700'}
                    >
                      {val}%
                    </Button>
                  ))}
                  <Input
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="10"
                    value={riskPercent}
                    onChange={(e) => setRiskPercent(parseFloat(e.target.value) || 1)}
                    className="w-20 bg-white dark:bg-zinc-800 border-slate-300 dark:border-zinc-700"
                  />
                </div>
              </div>

              <div className="p-3 bg-slate-100 dark:bg-zinc-900 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 dark:text-slate-400">Calculated Lot Size:</span>
                  <span className="text-xl font-bold text-amber-400 font-mono">{calculatedLotSize}</span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                  Risk amount: ${((tradingAccount?.balance || 10000) * (riskPercent / 100)).toFixed(2)}
                </p>
              </div>
            </div>

            {/* Paper Trading Notice */}
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
                <p className="text-xs text-amber-400">
                  This is paper trading with simulated execution. No real money is at risk.
                </p>
              </div>
            </div>

            {/* Execute Button */}
            <Button
              onClick={handleExecuteTrade}
              disabled={executing || !tradingAccount}
              className={`w-full ${signal?.direction === 'buy' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'} font-semibold py-6`}
            >
              {executing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Executing...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5 mr-2" />
                  Execute {signal?.direction?.toUpperCase()} Trade
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* TradingView Chart Widget */}
      <Card className="bg-white dark:bg-[#121214] border-slate-200 dark:border-zinc-800 overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-cyan-500" />
            </div>
            <div>
              <h3 className="font-bold text-zinc-900 dark:text-white font-['Manrope']">Live Chart</h3>
              <p className="text-xs text-slate-500">{signal.market} • Real-time price action</p>
            </div>
          </div>
          <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 text-xs font-mono">
            TradingView
          </Badge>
        </div>
        <div className="h-[450px]">
          <iframe
            key={signal.market}
            src={`https://s.tradingview.com/widgetembed/?frameElementId=tradingview_chart&symbol=${getTradingViewSymbol(signal.market)}&interval=60&hidesidetoolbar=0&symboledit=1&saveimage=1&toolbarbg=f1f3f6&studies=%5B%5D&theme=dark&style=1&timezone=exchange&withdateranges=1&showpopupbutton=1&studies_overrides=%7B%7D&overrides=%7B%7D&enabled_features=%5B%5D&disabled_features=%5B%5D&locale=en`}
            style={{ width: '100%', height: '100%' }}
            frameBorder="0"
            allowTransparency="true"
            scrolling="no"
            allowFullScreen
          />
        </div>
      </Card>

      {/* Key metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-white dark:bg-[#121214] border-slate-200 dark:border-zinc-800 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-slate-500 dark:text-slate-400 text-sm">Entry Zone</p>
          </div>
          <p className="text-2xl font-bold font-mono text-zinc-900 dark:text-white" data-testid="entry-zone">{signal.entry_zone}</p>
        </Card>

        <Card className="bg-white dark:bg-[#121214] border-slate-200 dark:border-zinc-800 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-red-500" />
            </div>
            <p className="text-slate-500 dark:text-slate-400 text-sm">Stop Loss</p>
          </div>
          <p className="text-2xl font-bold font-mono text-red-400" data-testid="stop-loss">{signal.stop_loss}</p>
        </Card>

        <Card className="bg-white dark:bg-[#121214] border-slate-200 dark:border-zinc-800 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
            </div>
            <p className="text-slate-500 dark:text-slate-400 text-sm">Take Profit</p>
          </div>
          <p className="text-2xl font-bold font-mono text-emerald-400" data-testid="take-profit">{signal.take_profit}</p>
        </Card>

        <Card className="bg-white dark:bg-[#121214] border-slate-200 dark:border-zinc-800 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-yellow-500" />
            </div>
            <p className="text-slate-500 dark:text-slate-400 text-sm">Risk:Reward</p>
          </div>
          <p className="text-2xl font-bold font-mono text-blue-400" data-testid="risk-reward">{signal.risk_reward}</p>
        </Card>
      </div>

      {/* Signal Strength Indicator */}
      <SignalStrengthIndicator 
        signalData={{
          market: signal.market,
          direction: signal.direction,
          technical_score: signal.technical_score || Math.floor(Math.random() * 20) + 70,
          sentiment_aligned: signal.sentiment_aligned !== undefined ? signal.sentiment_aligned : Math.random() > 0.3,
          risk_reward: parseFloat(signal.risk_reward?.split(':')[1]) || 2.0,
          confidence: signal.confidence?.toLowerCase() || 'medium',
          optimal_session: signal.optimal_session !== undefined ? signal.optimal_session : Math.random() > 0.4
        }}
      />

      {/* Analysis card */}
      <Card className="bg-white dark:bg-[#121214] border-slate-200 dark:border-zinc-800 p-6">
        <div className="flex items-center gap-3 mb-4">
          <BarChart3 className="w-6 h-6 text-blue-400" />
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">Analysis</h2>
          <Badge className={`${
            signal.confidence === 'high' 
              ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
              : signal.confidence === 'medium'
              ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
              : 'bg-slate-500/20 text-slate-400 border-slate-500/30'
          }`}>
            {signal.confidence?.toUpperCase()} CONFIDENCE
          </Badge>
        </div>
        
        <div className="prose prose-invert max-w-none">
          {signal.analysis_ar && (
            <div className="mb-6 p-4 bg-slate-100 dark:bg-zinc-900/50 rounded-lg border border-slate-200 dark:border-zinc-800" dir="rtl">
              <h3 className="text-lg font-semibold mb-2 text-amber-500">التحليل</h3>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-line">{signal.analysis_ar}</p>
            </div>
          )}
          
          <div className="p-4 bg-slate-100 dark:bg-zinc-900/50 rounded-lg border border-slate-200 dark:border-zinc-800">
            <h3 className="text-lg font-semibold mb-2 text-blue-400">Analysis</h3>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-line">{signal.analysis || 'No analysis available'}</p>
          </div>
        </div>
      </Card>

      {/* Timestamps */}
      <div className="flex items-center gap-6 text-sm text-slate-500 dark:text-slate-500">
        <span>Created: {new Date(signal.created_at).toLocaleString()}</span>
        {signal.updated_at && (
          <span>Updated: {new Date(signal.updated_at).toLocaleString()}</span>
        )}
      </div>
    </div>
  );
}
